import { gql, useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  useFormTemplateContext,
  useVisibilityContext,
} from "src/components/Providers/FormProvider";
import * as Question from "src/services/formTemplate/question";
import * as GQL from "src/types/graphql";

export const useRemoveAnswersForHiddenQuestions = ({
  formId,
}: {
  formId: string;
}) => {
  const { formTemplate } = useFormTemplateContext();

  const [clearAnswers] = useMutation<
    GQL.RemoveAnswersForHiddenQuestions,
    GQL.RemoveAnswersForHiddenQuestionsVariables
  >(
    gql`
      mutation RemoveAnswersForHiddenQuestions(
        $formId: uuid!
        $questionIds: [uuid!]!
      ) {
        delete_form_answer(
          where: {
            _and: {
              form_id: { _eq: $formId }
              form_question: {
                _and: {
                  question_id: { _in: $questionIds }
                  question: { question_type: { value: { _neq: "FileUpload" } } }
                }
              }
            }
          }
        ) {
          affected_rows
        }
      }
    `,
    {}
  );

  const visibilityContext = useVisibilityContext();

  const removeAnswersForHiddenQuestions = useCallback(async () => {
    const questions = Question.getFormCompleteQuestions(formTemplate);
    // Filter out archived questions - they should keep their answers even though hidden
    const nonArchivedQuestions = questions.filter((q) => !q.isArchived);
    const questionsNeedingAnswerCleanup =
      Question.findQuestionsNeedingAnswerCleanup(
        nonArchivedQuestions,
        visibilityContext
      );
    if (questionsNeedingAnswerCleanup.length > 0) {
      await clearAnswers({
        variables: {
          formId,
          questionIds: questionsNeedingAnswerCleanup.map((q) => q.id),
        },
      });
    }
  }, [clearAnswers, formId, formTemplate, visibilityContext]);

  return removeAnswersForHiddenQuestions;
};
